import { Component, OnInit } from '@angular/core';
import { BaseAuthComponent } from '../../../../layouts/guest/pages/home/components/base-auth.component';
import { RoutesPath } from '@shared/routes-path';
import { Response } from '@shared/models/response.model';
import { User } from '@shared/models/user/user.model';
import { Error } from '@shared/interfaces/error.interface';
import { Register } from '@shared/interfaces/auth/register-data.interface';
import { FacebookLoginProvider, SocialUser } from '@abacritt/angularx-social-login';
import { SocialMediaUser } from '@shared/interfaces/auth/social-media-user.interface';
import { TokenRefresh } from '@shared/interfaces/auth/token-refresh.interface';
import { AuthComponent } from '@layouts/default/components/auth/auth.component';
import { Login } from '@shared/interfaces/auth/login-data.interface';
import { HeaderService } from '@shared/services/header.service';
import { CustomLogin } from '@shared/interfaces/custom-login.interface';
import { MenuService } from '@shared/services/menu.service';

@Component({
  selector: 'app-register',
  templateUrl: './register.component.html',
  styleUrls: ['./register.component.scss'],
})
export class RegisterComponent extends BaseAuthComponent implements OnInit, CustomLogin {
  // Object was created to get provider without switch
  socialPlatforms: any = {
    FacebookLoginProvider,
  };
  password: string = null;
  authFromHelpUkraine: boolean = false;
  authWcd: boolean = false;

  constructor(
    public authComponent: AuthComponent,
    public header: HeaderService,
    public navigationService: MenuService
  ) {
    super();
  }

  ngOnInit() {
    this.layoutTitle = this.translate.instant('GLOBAL.welcome');
    this.authComponent.titlePageEmitter.emit(this.layoutTitle);
    this.authFromHelpUkraine = this.navigationService.isUkraineUrl();
    this.authWcd = this.navigationService.isWcd();
  }

  socialSignUp(socialPlatform: string) {
    this.socialMediaLoginWithoutGoogle(
      this.socialPlatforms[socialPlatform + 'LoginProvider'].PROVIDER_ID
    );
  }

  socialMediaLoginWithoutGoogle(provider: string) {
    if (provider.toLocaleLowerCase() !== 'google') {
      this.socialAuthService.signIn(provider).then((user: SocialUser) => {
        const pack: SocialMediaUser = {
          email: user.email,
          id: user.id,
          image: user.photoUrl,
          name: user.name,
          provider: user.provider.toLocaleLowerCase(),
          token: user.authToken,
        };
        const subLogin = this.authService.socialMediaAuth(pack).subscribe((token: TokenRefresh) => {
          this.storage.setToken(token);
          this.userAccess();
        }, this.onLoginError.bind(this));
        this.someSubscriptions.add(subLogin);
      });
    }
  }

  setUser() {
    const subUser = this.authService.userData().subscribe((resUser: Response<User>) => {
      const user = new User(resUser.data);
      this.storage.setUser(user);
      this.router.navigate([RoutesPath.default.home]);
    }, this.onRegisterError.bind(this));
    this.someSubscriptions.add(subUser);
  }

  register() {
    if (!this.form.invalid) {
      this.loading = true;
      this.password = this.form.value.plain_password;
      const pack: Register = {
        email: this.form.value.email,
        plain_password: this.form.value.plain_password,
        accept_term_of_service: this.form.value.confirm,
        type: 'normal',
      };
      const subRegister = this.authService.registerData(pack).subscribe((res: Response<User>) => {
        this.toast.success(
          this.translate.instant('AUTH.must_activate_account') + res.data.email,
          this.translate.instant('AUTH.register_success')
        );
        this.loginAfterRegister({ username: res.data.email, password: this.password });
        this.loading = false;
      }, this.onRegisterError.bind(this));
      this.someSubscriptions.add(subRegister);
    } else {
      this.toast.error(
        this.translate.instant('AUTH.wrong_username_or_password_or_tos'),
        this.translate.instant('AUTH.register_error')
      );
    }
  }

  onRegisterError(error: Error) {
    this.loading = false;
    if (error?.message === 'user_exists') {
      this.toast.error(
        this.translate.instant('AUTH.user_exists'),
        this.translate.instant('AUTH.register_error')
      );
    }
  }

  loginAfterRegister(pack: Login) {
    const subLogin = this.authService.loginData(pack).subscribe((token: TokenRefresh) => {
      this.storage.setToken(token);
      this.userAccess();
    }, this.onLoginError.bind(this));
    this.someSubscriptions.add(subLogin);
  }

  onLoginError(error: Error) {
    switch (error.message) {
      case 'account_blocked': {
        this.toast.error(
          this.translate.instant('AUTH.account_is_blocked'),
          this.translate.instant('AUTH.login_error')
        );
        break;
      }
      case 'wrong_token_passed': {
        this.toast.error(
          this.translate.instant('AUTH.password_will_not_be_changed'),
          this.translate.instant('AUTH.resetting_password_cancel')
        );
        break;
      }
      case 'user_exists': {
        this.toast.error(
          this.translate.instant('AUTH.user_exists'),
          this.translate.instant('AUTH.login_error')
        );
        this.socialAuthService.signOut();
        break;
      }
      case 'wrong_username_or_password': {
        this.toast.error(
          this.translate.instant('AUTH.wrong_username_or_password'),
          this.translate.instant('AUTH.login_error')
        );
        break;
      }
      case 'account_is_blocked_by_administration': {
        this.toast.error(
          this.translate.instant('AUTH.account_is_blocked_by_administration'),
          this.translate.instant('AUTH.login_error')
        );
        break;
      }
    }
    setTimeout(() => {
      this.loading = false;
    }, 1000);
  }

  userAccess() {
    const subUser = this.authService.userData().subscribe((resUser: Response<User>) => {
      const user = new User(resUser.data);
      this.storage.setUser(user);
      this.storage.setUserSettings({});
      this.languageService.updateCurrentLang(user.language.code);
      this.authComponent.closeAuthModal();

      this.toast.success(
        this.translate.instant('AUTH.welcome') + user.username,
        this.translate.instant('AUTH.login_success')
      );
      this.loading = false;

      if (this.authFromHelpUkraine) {
        this.storage.setAuthFromUkraine(true);
      } else if (this.authWcd) {
        this.storage.setAuthFromWcd(true);
      }
    }, this.onUserAccessError.bind(this));
    this.someSubscriptions.add(subUser);
  }

  onUserAccessError(error: Error) {
    this.loading = false;
    this.storage.logout();
    this.toast.error(
      this.translate.instant('AUTH.server_problem'),
      this.translate.instant('AUTH.login_error')
    );
  }
}
